/**
 * @module Model
 */

/**
 * Constant representing the country ID for the United States of America.
 *
 * This constant is used to identify the USA in various contexts, such as
 * when dealing with country-specific data or configurations.
 *
 * @const {number}
 */
export const USA_COUNTRY_ID = 233;

/**
 * Constant representing the country ID for Canada.
 *
 * This constant is used to identify Canada in various contexts, such as
 * when dealing with country-specific data or configurations.
 *
 * @const {number}
 */
export const CANADA_COUNTRY_ID = 38;

/**
 * Constant representing the currency symbol for the United States of America.
 *
 * This constant is used to display the currency symbol in user interfaces
 * and financial calculations related to USD.
 *
 * @const {string}
 */
export const USA_CURRENCY_SYMBOL = '$';

/**
 * Constant representing the currency code for the United States of America.
 *
 * This constant is used to identify the currency type in various contexts,
 * such as when dealing with financial transactions or currency conversions.
 *
 * @const {string}
 */
export const USA_CURRENCY_CODE = 'USD';

/**
 * Class representing a country.
 *
 * This class encapsulates various properties related to a country, including its ID,
 * code, currency information, alias, and name. It serves as a data structure for managing
 * country-related data within the application.
 *
 * @class
 * @model
 */
export class Country {
	/**
	 * The unique identifier for the country.
	 *
	 * @type {number}
	 */
	id: number;

	/**
	 * The ISO code of the country.
	 *
	 * @type {string}
	 */
	code: string;

	/**
	 * The currency code associated with the country (e.g., USD).
	 *
	 * @type {string}
	 */
	currencyCode: string;

	/**
	 * The currency symbol associated with the country (e.g., '$').
	 *
	 * @type {string}
	 */
	currencySymbol: string;

	/**
	 * An alternative name or alias for the country.
	 *
	 * @type {string}
	 */
	alias: string;

	/**
	 * The official name of the country.
	 *
	 * @type {string}
	 */
	name: string;

	/**
	 * Creates an instance of the Country.
	 *
	 * @constructor
	 */
	constructor() {}
}

/**
 * Class representing a state within a country.
 *
 * This class encapsulates various properties related to a state, including its ID,
 * code, alias, name, tax percentage, and the associated country ID. It serves as a
 * data structure for managing state-related data within the application.
 *
 * @class
 * @model
 */
export class State {
	/**
	 * The unique identifier for the state.
	 *
	 * @type {number}
	 */
	id: number;

	/**
	 * The ISO code of the state.
	 *
	 * @type {string}
	 */
	code: string;

	/**
	 * An alternative name or alias for the state.
	 *
	 * @type {string}
	 */
	alias: string;

	/**
	 * The official name of the state.
	 *
	 * @type {string}
	 */
	name: string;

	/**
	 * The tax percentage applicable to the state.
	 *
	 * @type {number}
	 */
	taxPercent: number;

	/**
	 * The unique identifier for the associated country.
	 *
	 * @type {number}
	 */
	countryId: number;

	/**
	 * Creates an instance of the State class.
	 *
	 * @constructor
	 */
	constructor() {}
}
